import * as React from 'react'
import { FC, useCallback } from 'react'
import { styled } from '@mui/material/styles'

import { listItemButtonClasses } from '@mui/material/ListItemButton'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  navIconSize,
  NavItem,
  navItemTextStyle,
  navItemTileStyle,
} from './NavItem'
import { NavTooltip } from './NavTooltip'

const selectedBorderColor = '#4c9aff'

// The selected item is an inset rounded tile with a light-blue outline and a
// short pill along its left edge. The tile geometry is shared with the buttons
// below the nav so every row rounds and insets the same way. `overflow: hidden`
// stops the min-content label painting outside the border below ~150px drawer
// width while the drawer animates open.
const SideNavLinkItem = styled(NavItem)(({ theme }) => ({
  ...navItemTileStyle,
  position: 'relative',
  padding: 0,
  flexGrow: 0,
  overflow: 'hidden',
  height: '45px',
  marginBottom: '5px',
  [`&.${listItemButtonClasses.selected}`]: {
    borderColor: selectedBorderColor,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '34px',
      borderRadius: '2px',
      backgroundColor: theme.palette.primary.contrastText,
    },
  },
}))

// icon: `flexShrink: 0` keeps the box from being squeezed in the 47px collapsed
// rail; the 5px left margin plus the tile's 5px margin and 1px border puts the
// icon 11px from the drawer edge, the same column as every other nav row.
const styles = {
  icon: {
    width: navIconSize,
    height: navIconSize,
    flexShrink: 0,
    margin: '0px 8px 0px 5px',
    '& > svg': {
      width: '25px',
      height: '25px',
    },
  },
  text: navItemTextStyle,
  textSelected: {
    ...navItemTextStyle,
    fontFamily: 'Source Sans Pro Semibold',
  },
}

interface Props {
  label: string
  url: string
  icon: React.ReactNode
  expanded: boolean
  selected: boolean
  handleLinkClick: (url: string) => void
  dataTestId?: string
}

export const SideNavLink: FC<Props> = (props) => {
  const { label, url, icon, expanded, selected, handleLinkClick, dataTestId } =
    props

  const onClick = useCallback(
    () => handleLinkClick(url),
    [handleLinkClick, url],
  )

  return (
    <NavTooltip label={label} expanded={expanded}>
      <SideNavLinkItem
        onClick={onClick}
        selected={selected}
        // `selected` only drives the visual state; this is what tells a screen
        // reader which destination is currently open.
        aria-current={selected ? 'page' : undefined}
        data-test-id={dataTestId}
      >
        <Box sx={styles.icon}>{icon}</Box>
        <Typography sx={selected ? styles.textSelected : styles.text}>
          {label}
        </Typography>
      </SideNavLinkItem>
    </NavTooltip>
  )
}
SideNavLink.displayName = 'SideNavLink'
